import { useState, useEffect } from "react";
import { Car, Trash2, Plus, Save, ChevronDown } from "lucide-react";
import "../css/AddVehicles.css";
import axios from 'axios';

const CLOUD_NAME = "dnriybfwk";
const UPLOAD_PRESET = "ofak_assets";


const getContentType = (file) => {
  if (file.type) return file.type;
  if (file.name.endsWith(".glb")) return "model/gltf-binary";
  if (file.name.endsWith(".pdf")) return "application/pdf";
  return "application/octet-stream";
};

const uploadToS3 = async (file) => {
  // 1. get a signed upload URL from backend
  const { data } = await axios.post(
    "/api/assets/upload-url",
    {
      fileName: file.name,
      contentType: getContentType(file),
    }
  );

  const { uploadUrl, fileUrl } = data.data;

  // 2. upload the file directly to S3 using the signed URL
  await axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": getContentType(file),
    },
  });

  // 3. return the CDN/public URL to store in form state
  return fileUrl;
};

const uploadToCloudinary = async (file, resourceType = "image") => {
  const data = new FormData();
  data.append("file", file);
  data.append("upload_preset", UPLOAD_PRESET);
  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${resourceType}/upload`,
    { method: "POST", body: data }
  );
  const result = await res.json();
  return result.secure_url;
};

const emptyColor  = () => ({ name: "", hex: "#0A2EA0", imageFile: null, glbFile: null });
const emptyDetail = () => ({ label: "", value: "" });
const emptyMetric = () => ({ label: "", value: "" });
const emptyStatePrice = (stateId = "", stateName = "") => ({
  state_id: stateId,
  stateName,
  values: {}
  // ex_showroom_price: "",
  // on_road_price: "",
});

export default function AddVehicles() {
  const [loading, setLoading]           = useState(false);
  const [allStates, setAllStates]       = useState([]);

  const [categoryName, setCategoryName] = useState("n-first");
  const [variantName, setVariantName]   = useState("");
  const [brochureFile, setBrochureFile] = useState(null);

  const [colors,      setColors]      = useState([emptyColor()]);
  const [details,     setDetails]     = useState([emptyDetail()]);
  const [metrics,     setMetrics]     = useState([emptyMetric()]);
  const [statePrices, setStatePrices] = useState([emptyStatePrice()]);
  const [pricingColumns, setPricingColumns] = useState([]);

  // Fetch states for the pricing dropdown
  useEffect(() => {
    // fetch("http://localhost:5000/api/states")
    fetch("/api/states")
      .then((r) => r.json())
      .then((d) => { if (d.success) setAllStates(d.data); })
      .catch(console.log);
  }, []);

  // ── Color handlers ──────────────────────────────────────────────
  const updateColor = (i, key, val) =>
    setColors((prev) => prev.map((c, idx) => idx === i ? { ...c, [key]: val } : c));
  const addColor    = () => setColors((p) => [...p, emptyColor()]);
  const removeColor = (i) => setColors((p) => p.filter((_, idx) => idx !== i));

  // ── Detail handlers ─────────────────────────────────────────────
  const updateDetail = (i, key, val) =>
    setDetails((prev) => prev.map((d, idx) => idx === i ? { ...d, [key]: val } : d));
  const addDetail    = () => setDetails((p) => [...p, emptyDetail()]);
  const removeDetail = (i) => setDetails((p) => p.filter((_, idx) => idx !== i));

  // ── Metric handlers ─────────────────────────────────────────────
  const updateMetric = (i, key, val) =>
    setMetrics((prev) => prev.map((m, idx) => idx === i ? { ...m, [key]: val } : m));
  const addMetric    = () => { if (metrics.length < 3) setMetrics((p) => [...p, emptyMetric()]); };
  const removeMetric = (i) => setMetrics((p) => p.filter((_, idx) => idx !== i));

  // ── State price handlers ────────────────────────────────────────
  const updateStatePrice = (i, key, val) =>
    setStatePrices((prev) => prev.map((sp, idx) => idx === i ? { ...sp, [key]: val } : sp));

  const handleStatePriceStateChange = (i, stateId) => {
    const found = allStates.find((s) => String(s.id) === String(stateId));
    setStatePrices((prev) =>
      prev.map((sp, idx) =>
        idx === i
          ? { ...sp, state_id: stateId, stateName: found ? found.name : "" }
          : sp
      )
    );
  };

  const addStatePrice = () => setStatePrices((p) => [...p, emptyStatePrice()]);

  const removeStatePrice = (i) =>
    setStatePrices((p) => p.filter((_, idx) => idx !== i));

  // IDs already chosen in other rows (to avoid duplicates)
  const usedStateIds = statePrices.map((sp) => String(sp.state_id)).filter(Boolean);

//   const addPricingColumn = () => {
//   if (pricingColumns.length >= 7) {
//     alert("Maximum 7 columns allowed");
//     return;
//   }

//   setPricingColumns([
//     ...pricingColumns,
//     ""
//   ]);
// };
const addPricingColumn = () => {
  if (pricingColumns.length >= 7) {
    alert("Maximum 7 columns allowed");
    return;
  }

  const newColumn = `Column ${pricingColumns.length + 1}`;

  setPricingColumns((prev) => [...prev, newColumn]);

  setStatePrices((prev) =>
    prev.map((row) => ({
      ...row,
      values: {
        ...row.values,
        [newColumn]: "",
      },
    }))
  );
};

// const updatePricingColumn = (index, value) => {
//   const updated = [...pricingColumns];
//   updated[index] = value;
//   setPricingColumns(updated);
// };
const updatePricingColumn = (index, value) => {
  const oldName = pricingColumns[index];

  const updated = [...pricingColumns];
  updated[index] = value;
  setPricingColumns(updated);

  setStatePrices((prev) =>
    prev.map((row) => {
      const values = { ...row.values };

      if (oldName in values) {
        values[value] = values[oldName];
        delete values[oldName];
      }

      return { ...row, values };
    })
  );
};

const removePricingColumn = (index) => {
  const columnName = pricingColumns[index];

  setPricingColumns(
    pricingColumns.filter((_, i) => i !== index)
  );

  setStatePrices((prev) =>
    prev.map((row) => {
      const values = { ...row.values };
      delete values[columnName];

      return {
        ...row,
        values
      };
    })
  );
};

const updateStateValue = (
  rowIndex,
  columnName,
  value
) => {
  setStatePrices((prev) =>
    prev.map((row, i) =>
      i === rowIndex
        ? {
            ...row,
            values: {
              ...row.values,
              [columnName]: value
            }
          }
        : row
    )
  );
};



  // ── Submit ──────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (colors.some((c) => !c.name.trim()))  return alert("Fill all color names");
    if (colors.some((c) => !c.imageFile))    return alert("Each color needs an image");
    if (details.some((d) => !d.label.trim() || !d.value.trim())) return alert("Fill all specification fields");
    if (metrics.some((m) => !m.label.trim() || !m.value.trim())) return alert("Fill all metric fields");
    if (statePrices.some((sp) => !sp.state_id))                  return alert("Select a state for every pricing row");
    // if (statePrices.some((sp) => !sp.ex_showroom_price || !sp.on_road_price))
    //   return alert("Fill both prices for every state");

    setLoading(true);
    try {
      const uploadedColors = await Promise.all(
        colors.map(async (c) => ({
          name:     c.name,
          hex:      c.hex,
          imageUrl: await uploadToS3(c.imageFile, "image"),
          glbUrl:   c.glbFile ? await uploadToS3(c.glbFile, "raw") : null,
        }))
      );

      const brochureUrl = brochureFile
        ? await uploadToS3(brochureFile, "raw")
        : null;

      const payload = {
        categoryName,
        variantName,
        brochureUrl,
        colors:  uploadedColors,
        details,
        metrics,
        statePrices: statePrices.map((sp) => ({
          state_id:         Number(sp.state_id),
          values: sp.values,
        })),
      };

      const res = await fetch("/api/vehicles", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Server error");

      alert("Vehicle added successfully!");
      setCategoryName("n-first");
      setVariantName("");
      setBrochureFile(null);
      setColors([emptyColor()]);
      setDetails([emptyDetail()]);
      setMetrics([emptyMetric()]);
      setStatePrices([emptyStatePrice()]);
    } catch (err) {
      alert("Something went wrong. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="av-page">
      <div className="av-header">
        <div className="av-header-left">
          <div className="av-header-icon"><Car size={20} /></div>
          <div>
            <h1 className="av-title">Add Vehicle</h1>
            <p className="av-subtitle">Fill in the details to list a new variant</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="av-form">

        {/* ── SECTION 1: Basic Info ── */}
        <div className="av-section">
          <div className="av-section-label">
            <span className="av-section-num">01</span>
            Basic Information
          </div>
          <div className="av-section-body">
            <div className="av-row-2">

              <div className="av-field">
                <label className="av-label">Category</label>
                <div className="av-select-wrap">
                  <select
                    className="av-select"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                  >
                    <option value="n-first">n-First</option>
                    <option value="diplos">Diplos</option>
                    <option value="institutional">Institutional</option>
                  </select>
                  <ChevronDown size={14} className="av-select-arrow" />
                </div>
              </div>

              <div className="av-field">
                <label className="av-label">Variant Name</label>
                <input
                  className="av-input"
                  type="text"
                  placeholder="e.g. i-max+"
                  value={variantName}
                  onChange={(e) => setVariantName(e.target.value)}
                  required
                />
              </div>

            </div>
          </div>
        </div>

        {/* ── SECTION 2: Colors ── */}
        <div className="av-section">
          <div className="av-section-label">
            <span className="av-section-num">02</span>
            Colors &amp; Assets
            <span className="av-section-hint">Each color has its own image and optional 3D model</span>
          </div>
          <div className="av-section-body">
            <div className="av-colors-list">
              {colors.map((c, i) => (
                <div key={i} className="av-color-card">
                  <div className="av-color-card-top">
                    <div className="av-color-swatch-wrap">
                      <input
                        type="color"
                        className="av-color-picker"
                        value={c.hex}
                        onChange={(e) => updateColor(i, "hex", e.target.value)}
                        title="Pick color"
                      />
                      <span className="av-color-hex">{c.hex}</span>
                    </div>
                    <input
                      className="av-input av-color-name"
                      type="text"
                      placeholder="Color name (e.g. Midnight Blue)"
                      value={c.name}
                      onChange={(e) => updateColor(i, "name", e.target.value)}
                    />
                    {colors.length > 1 && (
                      <button
                        type="button"
                        className="av-icon-btn av-danger"
                        onClick={() => removeColor(i)}
                        title="Remove color"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>

                  <div className="av-color-files">
                    <div className="av-file-field">
                      <label className="av-file-label">
                        Vehicle Image <span className="av-required">*</span>
                      </label>
                      <label className="av-file-box">
                        <input
                          type="file"
                          accept="image/*"
                          className="av-file-hidden"
                          onChange={(e) => updateColor(i, "imageFile", e.target.files[0])}
                        />
                        <span className="av-file-text">
                          {c.imageFile ? c.imageFile.name : "Choose image…"}
                        </span>
                        <span className="av-file-btn">Browse</span>
                      </label>
                    </div>

                    <div className="av-file-field">
                      <label className="av-file-label">
                        3D Model (.glb) <span className="av-optional">optional</span>
                      </label>
                      <label className="av-file-box">
                        <input
                          type="file"
                          accept=".glb"
                          className="av-file-hidden"
                          onChange={(e) => updateColor(i, "glbFile", e.target.files[0])}
                        />
                        <span className="av-file-text">
                          {c.glbFile ? c.glbFile.name : "Choose .glb file…"}
                        </span>
                        <span className="av-file-btn">Browse</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button type="button" className="av-add-btn" onClick={addColor}>
              <Plus size={15} /> Add Color
            </button>
          </div>
        </div>

        {/* ── SECTION 3: Specifications ── */}
        <div className="av-section">
          <div className="av-section-label">
            <span className="av-section-num">03</span>
            Specifications
            <span className="av-section-hint">Label–value pairs shown on the product card</span>
          </div>
          <div className="av-section-body">
            <div className="av-kv-list">
              {details.map((d, i) => (
                <div key={i} className="av-kv-row">
                  <input
                    className="av-input"
                    type="text"
                    placeholder="Label  (e.g. Battery Capacity)"
                    value={d.label}
                    onChange={(e) => updateDetail(i, "label", e.target.value)}
                  />
                  <input
                    className="av-input"
                    type="text"
                    placeholder="Value  (e.g. 3 kWh)"
                    value={d.value}
                    onChange={(e) => updateDetail(i, "value", e.target.value)}
                  />
                  {details.length > 1 && (
                    <button
                      type="button"
                      className="av-icon-btn av-danger"
                      onClick={() => removeDetail(i)}
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" className="av-add-btn" onClick={addDetail}>
              <Plus size={15} /> Add Specification
            </button>
          </div>
        </div>

        {/* ── SECTION 4: Key Metrics ── */}
        <div className="av-section">
          <div className="av-section-label">
            <span className="av-section-num">04</span>
            Key Metrics
            <span className="av-section-hint">Max 3 — shown as highlights on the card</span>
          </div>
          <div className="av-section-body">
            <div className="av-kv-list">
              {metrics.map((m, i) => (
                <div key={i} className="av-kv-row">
                  <input
                    className="av-input"
                    type="text"
                    placeholder="Label  (e.g. IDC Range)"
                    value={m.label}
                    onChange={(e) => updateMetric(i, "label", e.target.value)}
                  />
                  <input
                    className="av-input"
                    type="text"
                    placeholder="Value  (e.g. 109 km)"
                    value={m.value}
                    onChange={(e) => updateMetric(i, "value", e.target.value)}
                  />
                  {metrics.length > 1 && (
                    <button
                      type="button"
                      className="av-icon-btn av-danger"
                      onClick={() => removeMetric(i)}
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {metrics.length < 3 && (
              <button type="button" className="av-add-btn" onClick={addMetric}>
                <Plus size={15} /> Add Metric
              </button>
            )}
          </div>
        </div>

        {/* ── SECTION 5: State-wise Pricing ── */}
        <div className="av-section">
          <div className="av-section-label">
            <span className="av-section-num">05</span>
            State-wise Pricing
            <span className="av-section-hint">Create up to 7 pricing columns and assign values state-wise</span>
          </div>
          <div className="av-section-body">
            {/* Pricing Columns */}
    <div className="av-pricing-columns">

      <div className="av-pricing-columns-header">
        <h4>Pricing Columns</h4>
      </div>

      <div className="av-kv-list">
        {pricingColumns.map((column, index) => (
          <div key={index} className="av-kv-row">
            <input
              type="text"
              className="av-input"
              placeholder="Column Name"
              value={column}
              onChange={(e) =>
                updatePricingColumn(index, e.target.value)
              }
            />

            <button
              type="button"
              className="av-icon-btn av-danger"
              onClick={() => removePricingColumn(index)}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>

      {pricingColumns.length < 7 && (
        <button
          type="button"
          className="av-add-btn"
          onClick={addPricingColumn}
        >
          <Plus size={15} />
          Add Column
        </button>
      )}
    </div>

    {/* Dynamic Table Header */}
    <div
      className="av-price-header"
      style={{
        gridTemplateColumns: `220px repeat(${pricingColumns.length}, 1fr) 60px`
      }}
    >
      <span>State</span>

      {pricingColumns.map((column, index) => (
        <span key={index}>
          {column || `Column ${index + 1}`}
        </span>
      ))}

      <span></span>
    </div>

    {/* State Rows */}
    <div className="av-kv-list">
      {statePrices.map((sp, i) => (
        <div
          key={i}
          className="av-price-row"
          style={{
            display: "grid",
            gridTemplateColumns: `220px repeat(${pricingColumns.length}, 1fr) 60px`,
            gap: "12px",
            alignItems: "center"
          }}
        >
          {/* State */}
          <div className="av-select-wrap">
            <select
              className="av-select"
              value={sp.state_id}
              onChange={(e) =>
                handleStatePriceStateChange(i, e.target.value)
              }
            >
              <option value="">Select state...</option>

              {allStates.map((s) => (
                <option
                  key={s.id}
                  value={s.id}
                  disabled={
                    usedStateIds.includes(String(s.id)) &&
                    String(sp.state_id) !== String(s.id)
                  }
                >
                  {s.name} ({s.state_code})
                </option>
              ))}
            </select>

            <ChevronDown
              size={14}
              className="av-select-arrow"
            />
          </div>

          {/* Dynamic Pricing Inputs */}
          {pricingColumns.map((column, colIndex) => (
            <input
              key={colIndex}
              type="number"
              className="av-input"
              placeholder={column || "Value"}
              value={sp.values?.[column] || ""}
              onChange={(e) =>
                updateStateValue(
                  i,
                  column,
                  e.target.value
                )
              }
            />
          ))}

          {statePrices.length > 1 && (
            <button
              type="button"
              className="av-icon-btn av-danger"
              onClick={() => removeStatePrice(i)}
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      ))}
    </div>

    <button
      type="button"
      className="av-add-btn"
      onClick={addStatePrice}
      disabled={
        statePrices.length >= allStates.length &&
        allStates.length > 0
      }
    >
      <Plus size={15} />
      Add State
    </button>
           
          </div>
        </div>

        {/* ── SECTION 6: Brochure ── */}
        <div className="av-section">
          <div className="av-section-label">
            <span className="av-section-num">06</span>
            Brochure
            <span className="av-section-hint">PDF shown as download link on the product page</span>
          </div>
          <div className="av-section-body">
            <div className="av-file-field av-file-wide">
              <label className="av-file-label">
                Upload PDF <span className="av-optional">optional</span>
              </label>
              <label className="av-file-box">
                <input
                  type="file"
                  accept=".pdf"
                  className="av-file-hidden"
                  onChange={(e) => setBrochureFile(e.target.files[0])}
                />
                <span className="av-file-text">
                  {brochureFile ? brochureFile.name : "Choose PDF file…"}
                </span>
                <span className="av-file-btn">Browse</span>
              </label>
            </div>
          </div>
        </div>

        {/* ── Submit ── */}
        <div className="av-footer">
          <button type="submit" className="av-submit" disabled={loading}>
            {loading ? (
              <><span className="av-spinner" /> Uploading &amp; Saving…</>
            ) : (
              <><Save size={16} /> Save Vehicle</>
            )}
          </button>
        </div>

      </form>
    </div>
  );
}

// import { useState } from "react";
// import { Car, Trash2, Plus, Save, ChevronDown } from "lucide-react";
// import "../css/AddVehicles.css";

// const CLOUD_NAME = "dnriybfwk";
// const UPLOAD_PRESET = "ofak_assets";

// const uploadToCloudinary = async (file, resourceType = "image") => {
//   const data = new FormData();
//   data.append("file", file);
//   data.append("upload_preset", UPLOAD_PRESET);
//   const res = await fetch(
//     `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${resourceType}/upload`,
//     { method: "POST", body: data }
//   );
//   const result = await res.json();
//   return result.secure_url;
// };

// const emptyColor  = () => ({ name: "", hex: "#0A2EA0", imageFile: null, glbFile: null });
// const emptyDetail = () => ({ label: "", value: "" });
// const emptyMetric = () => ({ label: "", value: "" });

// export default function AddVehicles() {
//   const [loading, setLoading] = useState(false);

//   const [categoryName, setCategoryName] = useState("n-first");
//   const [variantName, setVariantName]   = useState("");
//   const [price, setPrice]               = useState("");
//   const [brochureFile, setBrochureFile] = useState(null);

//   const [colors,  setColors]   = useState([emptyColor()]);
//   const [details, setDetails]  = useState([emptyDetail()]);
//   const [metrics, setMetrics]  = useState([emptyMetric()]);

//   // Color handlers
//   const updateColor = (i, key, val) =>
//     setColors((prev) => prev.map((c, idx) => idx === i ? { ...c, [key]: val } : c));
//   const addColor    = () => setColors((p) => [...p, emptyColor()]);
//   const removeColor = (i) => setColors((p) => p.filter((_, idx) => idx !== i));

//   // Detail handlers
//   const updateDetail = (i, key, val) =>
//     setDetails((prev) => prev.map((d, idx) => idx === i ? { ...d, [key]: val } : d));
//   const addDetail    = () => setDetails((p) => [...p, emptyDetail()]);
//   const removeDetail = (i) => setDetails((p) => p.filter((_, idx) => idx !== i));

//   // Metric handlers
//   const updateMetric = (i, key, val) =>
//     setMetrics((prev) => prev.map((m, idx) => idx === i ? { ...m, [key]: val } : m));
//   const addMetric    = () => { if (metrics.length < 3) setMetrics((p) => [...p, emptyMetric()]); };
//   const removeMetric = (i) => setMetrics((p) => p.filter((_, idx) => idx !== i));

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (colors.some((c) => !c.name.trim())) return alert("Fill all color names");
//     if (colors.some((c) => !c.imageFile))   return alert("Each color needs an image");
//     if (details.some((d) => !d.label.trim() || !d.value.trim())) return alert("Fill all detail fields");
//     if (metrics.some((m) => !m.label.trim() || !m.value.trim())) return alert("Fill all metric fields");

//     setLoading(true);
//     try {
//       const uploadedColors = await Promise.all(
//         colors.map(async (c) => ({
//           name:     c.name,
//           hex:      c.hex,
//           imageUrl: await uploadToCloudinary(c.imageFile, "image"),
//           glbUrl:   c.glbFile ? await uploadToCloudinary(c.glbFile, "raw") : null,
//         }))
//       );

//       const brochureUrl = brochureFile
//         ? await uploadToCloudinary(brochureFile, "raw")
//         : null;

//       const payload = {
//         categoryName,
//         variantName,
//         price: Number(price),
//         brochureUrl,
//         colors:  uploadedColors,
//         details,
//         metrics,
//       };

//       const res = await fetch("http://localhost:5000/api/vehicles", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//       });

//       if (!res.ok) throw new Error("Server error");

//       alert("Vehicle added successfully!");
//       setCategoryName("n-first");
//       setVariantName("");
//       setPrice("");
//       setBrochureFile(null);
//       setColors([emptyColor()]);
//       setDetails([emptyDetail()]);
//       setMetrics([emptyMetric()]);
//     } catch (err) {
//       alert("Something went wrong. Please try again.");
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="av-page">
//       <div className="av-header">
//         <div className="av-header-left">
//           <div className="av-header-icon"><Car size={20} /></div>
//           <div>
//             <h1 className="av-title">Add Vehicle</h1>
//             <p className="av-subtitle">Fill in the details to list a new variant</p>
//           </div>
//         </div>
//       </div>

//       <form onSubmit={handleSubmit} className="av-form">

//         {/* ── SECTION 1: Basic Info ── */}
//         <div className="av-section">
//           <div className="av-section-label">
//             <span className="av-section-num">01</span>
//             Basic Information
//           </div>
//           <div className="av-section-body">
//             <div className="av-row-3">

//               <div className="av-field">
//                 <label className="av-label">Category</label>
//                 <div className="av-select-wrap">
//                   <select
//                     className="av-select"
//                     value={categoryName}
//                     onChange={(e) => setCategoryName(e.target.value)}
//                   >
//                     <option value="n-first">n-First</option>
//                     <option value="diplos">Diplos</option>
//                     <option value="institutional">Institutional</option>
//                   </select>
//                   <ChevronDown size={14} className="av-select-arrow" />
//                 </div>
//               </div>

//               <div className="av-field">
//                 <label className="av-label">Variant Name</label>
//                 <input
//                   className="av-input"
//                   type="text"
//                   placeholder="e.g. i-max+"
//                   value={variantName}
//                   onChange={(e) => setVariantName(e.target.value)}
//                   required
//                 />
//               </div>

//               <div className="av-field">
//                 <label className="av-label">Price (₹)</label>
//                 <input
//                   className="av-input"
//                   type="number"
//                   placeholder="e.g. 84999"
//                   value={price}
//                   onChange={(e) => setPrice(e.target.value)}
//                   required
//                 />
//               </div>

//             </div>
//           </div>
//         </div>

//         {/* ── SECTION 2: Colors ── */}
//         <div className="av-section">
//           <div className="av-section-label">
//             <span className="av-section-num">02</span>
//             Colors &amp; Assets
//             <span className="av-section-hint">Each color has its own image and optional 3D model</span>
//           </div>
//           <div className="av-section-body">
//             <div className="av-colors-list">
//               {colors.map((c, i) => (
//                 <div key={i} className="av-color-card">
//                   <div className="av-color-card-top">
//                     <div className="av-color-swatch-wrap">
//                       <input
//                         type="color"
//                         className="av-color-picker"
//                         value={c.hex}
//                         onChange={(e) => updateColor(i, "hex", e.target.value)}
//                         title="Pick color"
//                       />
//                       <span className="av-color-hex">{c.hex}</span>
//                     </div>
//                     <input
//                       className="av-input av-color-name"
//                       type="text"
//                       placeholder="Color name (e.g. Midnight Blue)"
//                       value={c.name}
//                       onChange={(e) => updateColor(i, "name", e.target.value)}
//                     />
//                     {colors.length > 1 && (
//                       <button
//                         type="button"
//                         className="av-icon-btn av-danger"
//                         onClick={() => removeColor(i)}
//                         title="Remove color"
//                       >
//                         <Trash2 size={15} />
//                       </button>
//                     )}
//                   </div>

//                   <div className="av-color-files">
//                     <div className="av-file-field">
//                       <label className="av-file-label">
//                         Vehicle Image <span className="av-required">*</span>
//                       </label>
//                       <label className="av-file-box">
//                         <input
//                           type="file"
//                           accept="image/*"
//                           className="av-file-hidden"
//                           onChange={(e) => updateColor(i, "imageFile", e.target.files[0])}
//                         />
//                         <span className="av-file-text">
//                           {c.imageFile ? c.imageFile.name : "Choose image…"}
//                         </span>
//                         <span className="av-file-btn">Browse</span>
//                       </label>
//                     </div>

//                     <div className="av-file-field">
//                       <label className="av-file-label">
//                         3D Model (.glb) <span className="av-optional">optional</span>
//                       </label>
//                       <label className="av-file-box">
//                         <input
//                           type="file"
//                           accept=".glb"
//                           className="av-file-hidden"
//                           onChange={(e) => updateColor(i, "glbFile", e.target.files[0])}
//                         />
//                         <span className="av-file-text">
//                           {c.glbFile ? c.glbFile.name : "Choose .glb file…"}
//                         </span>
//                         <span className="av-file-btn">Browse</span>
//                       </label>
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>

//             <button type="button" className="av-add-btn" onClick={addColor}>
//               <Plus size={15} /> Add Color
//             </button>
//           </div>
//         </div>

//         {/* ── SECTION 3: Details ── */}
//         <div className="av-section">
//           <div className="av-section-label">
//             <span className="av-section-num">03</span>
//             Specifications
//             <span className="av-section-hint">Label–value pairs shown on the product card</span>
//           </div>
//           <div className="av-section-body">
//             <div className="av-kv-list">
//               {details.map((d, i) => (
//                 <div key={i} className="av-kv-row">
//                   <input
//                     className="av-input"
//                     type="text"
//                     placeholder="Label  (e.g. Battery Capacity)"
//                     value={d.label}
//                     onChange={(e) => updateDetail(i, "label", e.target.value)}
//                   />
//                   <input
//                     className="av-input"
//                     type="text"
//                     placeholder="Value  (e.g. 3 kWh)"
//                     value={d.value}
//                     onChange={(e) => updateDetail(i, "value", e.target.value)}
//                   />
//                   {details.length > 1 && (
//                     <button
//                       type="button"
//                       className="av-icon-btn av-danger"
//                       onClick={() => removeDetail(i)}
//                     >
//                       <Trash2 size={15} />
//                     </button>
//                   )}
//                 </div>
//               ))}
//             </div>
//             <button type="button" className="av-add-btn" onClick={addDetail}>
//               <Plus size={15} /> Add Specification
//             </button>
//           </div>
//         </div>

//         {/* ── SECTION 4: Metrics ── */}
//         <div className="av-section">
//           <div className="av-section-label">
//             <span className="av-section-num">04</span>
//             Key Metrics
//             <span className="av-section-hint">Max 3 — shown as highlights on the card</span>
//           </div>
//           <div className="av-section-body">
//             <div className="av-kv-list">
//               {metrics.map((m, i) => (
//                 <div key={i} className="av-kv-row">
//                   <input
//                     className="av-input"
//                     type="text"
//                     placeholder="Label  (e.g. IDC Range)"
//                     value={m.label}
//                     onChange={(e) => updateMetric(i, "label", e.target.value)}
//                   />
//                   <input
//                     className="av-input"
//                     type="text"
//                     placeholder="Value  (e.g. 109 km)"
//                     value={m.value}
//                     onChange={(e) => updateMetric(i, "value", e.target.value)}
//                   />
//                   {metrics.length > 1 && (
//                     <button
//                       type="button"
//                       className="av-icon-btn av-danger"
//                       onClick={() => removeMetric(i)}
//                     >
//                       <Trash2 size={15} />
//                     </button>
//                   )}
//                 </div>
//               ))}
//             </div>
//             {metrics.length < 3 && (
//               <button type="button" className="av-add-btn" onClick={addMetric}>
//                 <Plus size={15} /> Add Metric
//               </button>
//             )}
//           </div>
//         </div>

//         {/* ── SECTION 5: Brochure ── */}
//         <div className="av-section">
//           <div className="av-section-label">
//             <span className="av-section-num">05</span>
//             Brochure
//             <span className="av-section-hint">PDF shown as download link on the product page</span>
//           </div>
//           <div className="av-section-body">
//             <div className="av-file-field av-file-wide">
//               <label className="av-file-label">
//                 Upload PDF <span className="av-optional">optional</span>
//               </label>
//               <label className="av-file-box">
//                 <input
//                   type="file"
//                   accept=".pdf"
//                   className="av-file-hidden"
//                   onChange={(e) => setBrochureFile(e.target.files[0])}
//                 />
//                 <span className="av-file-text">
//                   {brochureFile ? brochureFile.name : "Choose PDF file…"}
//                 </span>
//                 <span className="av-file-btn">Browse</span>
//               </label>
//             </div>
//           </div>
//         </div>

//         {/* ── Submit ── */}
//         <div className="av-footer">
//           <button type="submit" className="av-submit" disabled={loading}>
//             {loading ? (
//               <><span className="av-spinner" /> Uploading &amp; Saving…</>
//             ) : (
//               <><Save size={16} /> Save Vehicle</>
//             )}
//           </button>
//         </div>

//       </form>
//     </div>
//   );
// }

